'use client';

import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { Warning, X } from '@phosphor-icons/react';
import { Button } from './Button';
import { cn } from '../../utils/cn';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
  requireTextMatch?: string;
  matchInputLabel?: string;
  matchInputPlaceholder?: string;
  matchErrorText?: string;
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title = '确认操作',
  description = '您确定要执行此操作吗？',
  confirmText = '确认',
  cancelText = '取消',
  type = 'warning',
  requireTextMatch,
  matchInputLabel = '请输入确认文本',
  matchInputPlaceholder = '请输入文本',
  matchErrorText = '输入内容不匹配',
}: ConfirmDialogProps) {
  const [inputValue, setInputValue] = React.useState('');
  const requiresMatch = Boolean(requireTextMatch);
  const isMatchValid = !requiresMatch || inputValue === requireTextMatch;

  React.useEffect(() => {
    if (!isOpen) {
      setInputValue('');
    }
  }, [isOpen]);

  const typeStyles = {
    danger: {
      icon: 'bg-red-100 text-red-600',
      button: 'danger' as const,
    },
    warning: {
      icon: 'bg-amber-100 text-amber-600',
      button: 'accent' as const,
    },
    info: {
      icon: 'bg-blue-100 text-blue-600',
      button: 'primary' as const,
    },
  };

  const currentType = typeStyles[type] ?? typeStyles.warning;

  const handleConfirm = () => {
    if (!isMatchValid) return;
    onConfirm();
    onClose();
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <AnimatePresence>
          {isOpen && (
            <>
              {/* Backdrop */}
              <Dialog.Overlay asChild>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm"
                />
              </Dialog.Overlay>

              {/* Dialog Content */}
              <Dialog.Content asChild>
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                  className={cn(
                    'fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2',
                    'rounded-3xl bg-white p-6 shadow-2xl shadow-zinc-900/20'
                  )}
                >
                  {/* Close Button */}
                  <Dialog.Close asChild>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="absolute right-4 top-4 rounded-full p-2 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
                    >
                      <X className="h-5 w-5" />
                    </motion.button>
                  </Dialog.Close>

                  {/* Icon */}
                  <div className="mb-4 flex items-center gap-4">
                    <div
                      className={cn(
                        'flex h-12 w-12 items-center justify-center rounded-2xl',
                        currentType.icon
                      )}
                    >
                      <Warning className="h-6 w-6" weight="fill" />
                    </div>
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-zinc-900">
                        {title}
                      </Dialog.Title>
                    </div>
                  </div>

                  {/* Description */}
                  <Dialog.Description className="mb-6 text-sm leading-relaxed text-zinc-500">
                    {description}
                  </Dialog.Description>

                  {requiresMatch && (
                    <div className="mb-6">
                      <label className="mb-2 block text-xs font-medium text-zinc-700">
                        {matchInputLabel}
                      </label>
                      <input
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        placeholder={matchInputPlaceholder}
                        className={cn(
                          'w-full rounded-xl border px-3 py-2 text-sm outline-none transition-colors',
                          isMatchValid
                            ? 'border-zinc-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20'
                            : 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                        )}
                      />
                      {!isMatchValid && inputValue.length > 0 && (
                        <p className="mt-2 text-xs text-red-500">{matchErrorText}</p>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-end gap-3">
                    <Button variant="secondary" onClick={onClose}>
                      {cancelText}
                    </Button>
                    <Button
                      variant={currentType.button}
                      onClick={handleConfirm}
                      disabled={!isMatchValid}
                    >
                      {confirmText}
                    </Button>
                  </div>
                </motion.div>
              </Dialog.Content>
            </>
          )}
        </AnimatePresence>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// Hook for using confirm dialog
export function useConfirm() {
  const [isOpen, setIsOpen] = React.useState(false);
  const resolveRef = React.useRef<((value: boolean) => void) | null>(null);
  const [config, setConfig] = React.useState<{
    title?: string;
    description?: string;
    confirmText?: string;
    cancelText?: string;
    type?: 'danger' | 'warning' | 'info';
    requireTextMatch?: string;
    matchInputLabel?: string;
    matchInputPlaceholder?: string;
    matchErrorText?: string;
    onConfirm: () => void;
  }>({ onConfirm: () => {} });

  const confirm = React.useCallback(
    (options: Omit<typeof config, 'onConfirm'>) => {
      return new Promise<boolean>((resolve) => {
        resolveRef.current = resolve;
        setConfig({
          ...options,
          onConfirm: () => {
            resolveRef.current?.(true);
            resolveRef.current = null;
          },
        });
        setIsOpen(true);
      });
    },
    []
  );

  const handleClose = React.useCallback(() => {
    if (resolveRef.current) {
      resolveRef.current(false);
      resolveRef.current = null;
    }
    setIsOpen(false);
  }, []);

  const handleConfirm = React.useCallback(() => {
    config.onConfirm();
    setIsOpen(false);
  }, [config]);

  const dialog = (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
      title={config.title}
      description={config.description}
      confirmText={config.confirmText}
      cancelText={config.cancelText}
      type={config.type}
      requireTextMatch={config.requireTextMatch}
      matchInputLabel={config.matchInputLabel}
      matchInputPlaceholder={config.matchInputPlaceholder}
      matchErrorText={config.matchErrorText}
    />
  );

  return { confirm, dialog };
}
